import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import { createPersonalSheet, resolvedPersonalSheet, personalSheetCalculationErrors } from '../utils/personal-sheet'
import { SPOUSE_AGES } from '../utils/spouse-ages'

Vue.use(Vuex)

function normalizePeopleCount(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 1
  return Math.min(Math.max(Math.round(n), 1), 4)
}

function utilitySelectedPeople(state, utilityKind) {
  const map = {
    electricity: state.people_electricity,
    gas: state.people_gas,
    water: state.people_water
  }
  const selected = map[utilityKind]
  if (hasUtilityPeopleSelected(state, utilityKind)) {
    return normalizePeopleCount(selected)
  }

  const persona = (state.login_response && state.login_response.persona) || {}
  return normalizePeopleCount(persona.people)
}

function hasUtilityPeopleSelected(state, utilityKind) {
  const map = {
    electricity: state.people_electricity,
    gas: state.people_gas,
    water: state.people_water
  }
  const selected = map[utilityKind]
  const n = Number(selected)
  return Number.isFinite(n) && n >= 1
}

function utilitySelectLabelByPeople() {
  return '使用量'
}

function utilityBaseCost(state, utilityKind) {
  const house = state.selected_house || {}
  const persona = (state.login_response && state.login_response.persona) || {}

  const square = Number(house.square) || 0
  const nmi = Number(persona.net_monthly_income) || 0
  const people = utilitySelectedPeople(state, utilityKind)
  const extraFamilyCost = Math.max(people - 1, 0) * 500

  const perSquare = utilityKind === 'water' ? 40 : 50
  const nmiRateMap = {
    electricity: 0.0072,
    gas: 0.007,
    water: 0.008
  }
  const nmiRate = nmiRateMap[utilityKind] || 0

  return Math.round(square * perSquare + nmi * nmiRate + extraFamilyCost)
}

function syncUtilityBreakdown(state, utilityKind) {
  const selectedMap = state.utility_selected || {}
  const costMap = state.utility_selected_cost || {}
  const isSelected = !!selectedMap[utilityKind]

  if (!isSelected) {
    state.breakdown[utilityKind] = 0
    return
  }

  const selectedCost = Number(costMap[utilityKind]) || 0
  state.breakdown[utilityKind] = utilityBaseCost(state, utilityKind) + selectedCost
}

export default new Vuex.Store({
  data: {
    i: 0
  },
  state: {
    key: 0,
    isFetching: false,
    login_response: {},
    submitted_summary: null,
    personal_sheet: createPersonalSheet(),
    smartphone_plans: {},
    people_electricity: 0,
    people_gas: 0,
    people_water: 0,
    utility_selected: {
      electricity: false,
      gas: false,
      water: false
    },
    utility_selected_cost: {
      electricity: 0,
      gas: 0,
      water: 0
    },
    selected_house: null,
    electricity_house_type: null,
    error: null,
    breakdown: {
      balance: 0,
      house: 0,
      electricity: 0,
      gas: 0,
      water: 0,
      main_food1: 0,
      main_food2: 0,
      main_food3: 0,
      main_food4: 0,
      side_food1: 0,
      side_food2: 0,
      side_food3: 0,
      side_food4: 0,
      fashion_men: new Array(),
      fashion_women: new Array(),
      fashion_kids: new Array(),
      fashion_baby: new Array(),
      car: 0,
      smartphone_adult: 0,
      smartphone_kids: 0,
      media_newspaper: 0,
      media_tv_radio: 0,
      media_books: 0,
      media_web: 0,
      media_isp: 0,
      daily_necessities_dental: 0,
      daily_necessities_laundry: 0,
      daily_necessities_sanitary: 0,
      daily_necessities_bath: 0,
      daily_necessities_kitchen: 0,
      daily_necessities_etc: 0,
      medical_medicine_internal: 0,
      medical_medicine_analgesic: 0,
      medical_medicine_external: 0,
      medical_medicine_etc: 0,
      medical_medicine_supplement: 0,
      medical_medicine_children: 0,
      medical_physical: new Array(),
      saving: 0,
      life_insurance_you: 0,
      life_insurance_spouse: 0,
      eat_out: new Array(),
      lesson_adult: new Array(),
      lesson_child: new Array(),
      emergency_kit: 0,
      trip: new Array()
    },
    progresses: {
      progress_house: false,
      progress_electricity: false,
      progress_gas: false,
      progress_water: false,
      progress_food: false,
      progress_fashion: false,
      progress_car: false,
      progress_smartphone: false,
      progress_media: false,
      progress_daily_necessities: false,
      progress_medical: false,
      progress_saving: false,
      progress_life_insurance: false,
      progress_eat_out: false,
      progress_lesson: false,
      progress_emergency_kit: false,
      progress_trip: false
    },
    select: {
      house: "選択してください",
      electricity: "家族人数を選択してください",
      gas: "家族人数を選択してください",
      water: "家族人数を選択してください",
      main_food: [
        "選択してください",
        "選択してください",
        "選択してください",
        "選択してください"
      ],
      side_food: [
        "選択してください",
        "選択してください",
        "選択してください",
        "選択してください"
      ],
      fashion_men: new Array(100).fill('0'),
      fashion_women: new Array(100).fill('0'),
      fashion_kids: new Array(100).fill('0'),
      fashion_baby: new Array(100).fill('0'),
      car: "選択してください",
      smartphone: {
        adult: "選択してください",
        kids: "選択してください"
      },
      media: {
        newspaper: [],
        tv_radio: [],
        books: [],
        web: [],
        isp: []
      },
      daily_necessities: {
        dental: [],
        laundry: [],
        sanitary: [],
        bath: [],
        kitchen: [],
        etc: []
      },
      medical_medicine: {
        internal: [],
        analgesic: [],
        external: [],
        etc: [],
        supplement: [],
        children: []
      },
      medical_physical: new Array(100).fill('選択してください'),
      saving: "貯金額を選択してください",
      life_insurance: {
        you: "選択してください",
        spouse: "選択してください"
      },
      eat_out: new Array(100).fill('0'),
      lesson: {
        adult: new Array(100).fill('0'),
        child: new Array(100).fill('0')
      },
      emergency_kit:"選択してください",
      trip: {
        adult: new Array(100).fill('大人料金0'),
        child: new Array(100).fill('子ども料金0')
      }
    },
    weeks: 4,
    progress: 0,
    contents: 17,
    target: null,
    env: null,
    is_debug: false
  },
  mutations: {
    confirm_personal_sheet_totals(state) {
      const errors = personalSheetCalculationErrors(resolvedPersonalSheet(state))
      if (Object.keys(errors.fields).length || Object.keys(errors.amounts).length) return
      if (!state.personal_sheet) Vue.set(state, 'personal_sheet', createPersonalSheet())
      Vue.set(state.personal_sheet, 'totals_confirmed', true)
    },
    set_personal_sheet_spouse(state, value) {
      const selection = value == null ? '' : String(value)
      if (selection !== '' && selection !== 'none' && !SPOUSE_AGES.map(String).includes(selection)) return
      if (!state.personal_sheet) Vue.set(state, 'personal_sheet', createPersonalSheet())
      Vue.set(state.personal_sheet.fields, 'is_married', selection === '' ? '' : selection === 'none' ? 'いない' : 'いる')
      Vue.set(state.personal_sheet.fields, 'spouse_age', selection === '' || selection === 'none' ? '' : selection)
    },
    set_personal_sheet_field(state, { section, key, value }) {
      if (!state.personal_sheet) Vue.set(state, 'personal_sheet', createPersonalSheet())
      if (!['fields', 'amounts'].includes(section)) return
      // Validate against the schema so older saved sheets can accept newly added fields.
      if (!Object.prototype.hasOwnProperty.call(createPersonalSheet()[section], key)) return
      Vue.set(state.personal_sheet[section], key, value)
    },
    set_login_response: function(state, value) {
      state.login_response = value;
      Vue.set(state, 'smartphone_plans', {});
      state.breakdown.smartphone_adult = 0;
      state.breakdown.smartphone_kids = 0;
      state.select.smartphone.adult = '選択してください';
      state.select.smartphone.kids = '選択してください';
      Vue.set(state, 'personal_sheet', createPersonalSheet());
      state.submitted_summary = null;
      console.log('state.login_response', state.login_response);

      state.utility_selected = state.utility_selected || { electricity: false, gas: false, water: false }
      state.utility_selected_cost = state.utility_selected_cost || { electricity: 0, gas: 0, water: 0 }
      state.utility_selected.electricity = !!state.utility_selected.electricity
      state.utility_selected.gas = !!state.utility_selected.gas
      state.utility_selected.water = !!state.utility_selected.water
      state.utility_selected_cost.electricity = Number(state.utility_selected_cost.electricity) || 0
      state.utility_selected_cost.gas = Number(state.utility_selected_cost.gas) || 0
      state.utility_selected_cost.water = Number(state.utility_selected_cost.water) || 0

      state.people_electricity = 0
      state.people_gas = 0
      state.people_water = 0
      state.utility_selected.electricity = false
      state.utility_selected.gas = false
      state.utility_selected.water = false
      state.utility_selected_cost.electricity = 0
      state.utility_selected_cost.gas = 0
      state.utility_selected_cost.water = 0
      state.select.electricity = utilitySelectLabelByPeople(state, 'electricity')
      state.select.gas = utilitySelectLabelByPeople(state, 'gas')
      state.select.water = utilitySelectLabelByPeople(state, 'water')
      state.breakdown.electricity = 0
      state.breakdown.gas = 0
      state.breakdown.water = 0
      console.log('state.people_electricity', state.people_electricity);
      console.log('state.people_gas', state.people_gas);
      console.log('state.people_water', state.people_water);
    },
    set_balance: function(state, value) {
      state.breakdown.balance = value;
      console.log('state.breakdown.balance', state.breakdown.balance);
    },
    set_submitted_summary: function(state, value) {
      state.submitted_summary = { ...value };
      console.log('state.submitted_summary', state.submitted_summary);
    },
    rebalance: function(state) {
      state.breakdown.balance = 
      state.login_response.persona.net_monthly_income + 
      state.login_response.persona.lucky_card - 
      state.breakdown.house - 
      state.breakdown.electricity - 
      state.breakdown.gas - 
      state.breakdown.water -
      state.breakdown.main_food1 - 
      state.breakdown.main_food2 - 
      state.breakdown.main_food3 - 
      state.breakdown.main_food4 - 
      state.breakdown.side_food1 - 
      state.breakdown.side_food2 - 
      state.breakdown.side_food3 - 
      state.breakdown.side_food4 - 
      state.breakdown.fashion_men.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.fashion_women.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.fashion_kids.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.fashion_baby.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.car - 
      state.breakdown.smartphone_adult - 
      state.breakdown.smartphone_kids - 
      state.breakdown.media_newspaper - 
      state.breakdown.media_tv_radio - 
      state.breakdown.media_books - 
      state.breakdown.media_web - 
      state.breakdown.media_isp - 
      state.breakdown.daily_necessities_dental - 
      state.breakdown.daily_necessities_laundry - 
      state.breakdown.daily_necessities_sanitary - 
      state.breakdown.daily_necessities_bath - 
      state.breakdown.daily_necessities_kitchen - 
      state.breakdown.daily_necessities_etc - 
      state.breakdown.medical_medicine_internal - 
      state.breakdown.medical_medicine_analgesic - 
      state.breakdown.medical_medicine_external - 
      state.breakdown.medical_medicine_etc - 
      state.breakdown.medical_medicine_supplement - 
      state.breakdown.medical_medicine_children - 
      state.breakdown.medical_physical.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.saving - 
      state.breakdown.life_insurance_you - 
      state.breakdown.life_insurance_spouse - 
      state.breakdown.eat_out.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.lesson_adult.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.lesson_child.reduce((sum, i) => { return sum + i.order; }, 0) - 
      state.breakdown.emergency_kit - 
      state.breakdown.trip.reduce((sum, i) => { return sum + i.order_adult; }, 0) - 
      state.breakdown.trip.reduce((sum, i) => { return sum + i.order_child; }, 0);
      state.breakdown.balance = parseInt(state.breakdown.balance);
      console.log('state.breakdown.balance', state.breakdown.balance);
    },
    value_house: function(state, value) {
      state.breakdown.house = value.rent;
      console.log('state.breakdown.house', state.breakdown.house);
      state.select.house = value.select;
      console.log('state.select.house', state.select.house);
      state.selected_house = value;
      console.log('state.selected_house', state.selected_house);
    },
    value_electricity: function(state) {
      const rent = state.breakdown.house;
      let houseType = null;

      if (rent > 0 && rent <= 40000) {
        houseType = 1;
      } else if (rent <= 50000) {
        houseType = 2;
      } else if (rent <= 60000) {
        houseType = 3;
      } else if (rent <= 70000) {
        houseType = 4;
      } else if (rent <= 80000) {
        houseType = 5;
      } else if (rent <= 90000) {
        houseType = 6;
      } else if (rent <= 100000) {
        houseType = 7;
      } else if (rent <= 110000) {
        houseType = 8;
      } else {
        houseType = null;
      }

      state.electricity_house_type = houseType;
      console.log('state.electricity_house_type', state.electricity_house_type);

      // 家を変更した場合は電気プランをリセット
      state.breakdown.electricity = 0;
      state.utility_selected.electricity = false;
      state.utility_selected_cost.electricity = 0;
      state.select.electricity = utilitySelectLabelByPeople(state, 'electricity');
      console.log('state.breakdown.electricity', state.breakdown.electricity);
      console.log('state.select.electricity', state.select.electricity);

      // 家を変更した場合はガス/水道もリセット（面積ベース計算の再選択を促す）
      state.breakdown.gas = 0;
      state.utility_selected.gas = false;
      state.utility_selected_cost.gas = 0;
      state.select.gas = utilitySelectLabelByPeople(state, 'gas');
      console.log('state.breakdown.gas', state.breakdown.gas);
      console.log('state.select.gas', state.select.gas);
      state.breakdown.water = 0;
      state.utility_selected.water = false;
      state.utility_selected_cost.water = 0;
      state.select.water = utilitySelectLabelByPeople(state, 'water');
      console.log('state.breakdown.water', state.breakdown.water);
      console.log('state.select.water', state.select.water);
    },
    set_utility_people: function(state, { kind, people }) {
      const n = Number(people)
      const normalized = (Number.isFinite(n) && n >= 1 && n <= 4) ? Math.round(n) : 0

      if (kind === 'electricity') {
        state.people_electricity = normalized
      } else if (kind === 'gas') {
        state.people_gas = normalized
      } else if (kind === 'water') {
        state.people_water = normalized
      } else {
        return
      }
      console.log(`state.people_${kind}`, normalized)
    },
    clear_utility_plan: function(state, { kind, selectLabel }) {
      state.utility_selected = state.utility_selected || { electricity: false, gas: false, water: false }
      state.utility_selected_cost = state.utility_selected_cost || { electricity: 0, gas: 0, water: 0 }
      if (!Object.prototype.hasOwnProperty.call(state.utility_selected, kind)) return
      state.utility_selected[kind] = false
      state.utility_selected_cost[kind] = 0
      state.breakdown[kind] = 0

      if (kind === 'electricity') {
        state.select.electricity = selectLabel || utilitySelectLabelByPeople(state, 'electricity')
      } else if (kind === 'gas') {
        state.select.gas = selectLabel || utilitySelectLabelByPeople(state, 'gas')
      } else if (kind === 'water') {
        state.select.water = selectLabel || utilitySelectLabelByPeople(state, 'water')
      }
    },
    apply_electricity_plan: function(state, value) {
      // value = { cost, select }
      const selectedCost = Number(value.cost) || 0
      state.utility_selected.electricity = true
      state.utility_selected_cost.electricity = selectedCost
      syncUtilityBreakdown(state, 'electricity')
      console.log('state.breakdown.electricity', state.breakdown.electricity);
      state.select.electricity = value.select;
      console.log('state.select.electricity', state.select.electricity);
    },
    value_gas: function(state, value) {
      const selectedCost = Number(value.cost) || 0
      state.utility_selected.gas = true
      state.utility_selected_cost.gas = selectedCost
      syncUtilityBreakdown(state, 'gas')
      console.log('state.breakdown.gas', state.breakdown.gas);
      state.select.gas = value.select;
      console.log('state.select.gas', state.select.gas);
    },
    value_water: function(state, value) {
      const selectedCost = Number(value.cost) || 0
      state.utility_selected.water = true
      state.utility_selected_cost.water = selectedCost
      syncUtilityBreakdown(state, 'water')
      console.log('state.breakdown.water', state.breakdown.water);
      state.select.water = value.select;
      console.log('state.select.water', state.select.water);
    },
    value_main_food1: function(state, value) {
      state.breakdown.main_food1 = value.cost;
      console.log('state.breakdown.main_food1', state.breakdown.main_food1);
      state.select.main_food[0] = value.course;
      console.log('state.select.main_food[0]', state.select.main_food[0]);
    },
    value_main_food2: function(state, value) {
      state.breakdown.main_food2 = value.cost;
      console.log('state.breakdown.main_food2', state.breakdown.main_food2);
      state.select.main_food[1] = value.course;
      console.log('state.select.main_food[1]', state.select.main_food[1]);
    },
    value_main_food3: function(state, value) {
      state.breakdown.main_food3 = value.cost;
      console.log('state.breakdown.main_food3', state.breakdown.main_food3);
      state.select.main_food[2] = value.course;
      console.log('state.select.main_food[2]', state.select.main_food[2]);
    },
    value_main_food4: function(state, value) {
      state.breakdown.main_food4 = value.cost;
      console.log('state.breakdown.main_food4', state.breakdown.main_food4);
      state.select.main_food[3] = value.course;
      console.log('state.select.main_food[3]', state.select.main_food[3]);
    },
    value_side_food1: function(state, value) {
      state.breakdown.side_food1 = value.cost;
      console.log('state.breakdown.side_food1', state.breakdown.side_food1);
      state.select.side_food[0] = value.course;
      console.log('state.select.side_food[0]', state.select.side_food[0]);
    },
    value_side_food2: function(state, value) {
      state.breakdown.side_food2 = value.cost;
      console.log('state.breakdown.side_food2', state.breakdown.side_food2);
      state.select.side_food[1] = value.course;
      console.log('state.select.side_food[1]', state.select.side_food[1]);
    },
    value_side_food3: function(state, value) {
      state.breakdown.side_food3 = value.cost;
      console.log('state.breakdown.side_food3', state.breakdown.side_food3);
      state.select.side_food[2] = value.course;
      console.log('state.select.side_food[2]', state.select.side_food[2]);
    },
    value_side_food4: function(state, value) {
      state.breakdown.side_food4 = value.cost;
      console.log('state.breakdown.side_food4', state.breakdown.side_food4);
      state.select.side_food[3] = value.course;
      console.log('state.select.side_food[3]', state.select.side_food[3]);
    },
    initialize_fashion_men: function(state, value) {
      state.breakdown.fashion_men = value;
    },
    value_fashion_men: function(state, {index, num}) {
      state.breakdown.fashion_men[index].num = num;
      state.breakdown.fashion_men[index].order = state.breakdown.fashion_men[index].cost * state.breakdown.fashion_men[index].num;
      console.log('state.breakdown.fashion_men.reduce', state.breakdown.fashion_men.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.fashion_men[index] = String(num);
      console.log('state.select.fashion_men[index]', state.select.fashion_men[index]);
    },
    initialize_fashion_women: function(state, value) {
      state.breakdown.fashion_women = value;
    },
    value_fashion_women: function(state, {index, num}) {
      state.breakdown.fashion_women[index].num = num;
      state.breakdown.fashion_women[index].order = state.breakdown.fashion_women[index].cost * state.breakdown.fashion_women[index].num;
      console.log('state.breakdown.fashion_women.reduce', state.breakdown.fashion_women.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.fashion_women[index] = String(num);
      console.log('state.select.fashion_women[index]', state.select.fashion_women[index]);
    },
    initialize_fashion_kids: function(state, value) {
      state.breakdown.fashion_kids = value;
    },
    value_fashion_kids: function(state, {index, num}) {
      state.breakdown.fashion_kids[index].num = num;
      state.breakdown.fashion_kids[index].order = state.breakdown.fashion_kids[index].cost * state.breakdown.fashion_kids[index].num;
      console.log('state.breakdown.fashion_kids.reduce', state.breakdown.fashion_kids.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.fashion_kids[index] = String(num);
      console.log('state.select.fashion_kids[index]', state.select.fashion_kids[index]);
    },
    initialize_fashion_baby: function(state, value) {
      state.breakdown.fashion_baby = value;
    },
    value_fashion_baby: function(state, {index, num}) {
      state.breakdown.fashion_baby[index].num = num;
      state.breakdown.fashion_baby[index].order = state.breakdown.fashion_baby[index].cost * state.breakdown.fashion_baby[index].num;
      console.log('state.breakdown.fashion_baby.reduce', state.breakdown.fashion_baby.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.fashion_baby[index] = String(num);
      console.log('state.select.fashion_baby[index]', state.select.fashion_baby[index]);
    },
    value_car: function(state, value) {
      state.breakdown.car = value.cost;
      console.log('state.breakdown.car', state.breakdown.car);
      state.select.car = value.model;
      console.log('state.select.car', state.select.car);
    },
    set_smartphone_plan: function(state, { key, plan }) {
      if (!['self', 'spouse', 'child1', 'child2'].includes(key)) return;
      if (!state.smartphone_plans) Vue.set(state, 'smartphone_plans', {});
      Vue.set(state.smartphone_plans, key, plan);
    },
    value_smartphone_adult: function(state, value) {
      state.breakdown.smartphone_adult = value.cost;
      console.log('state.breakdown.smartphone_adult', state.breakdown.smartphone_adult);
      state.select.smartphone.adult = value.plan;
      console.log('state.select.smartphone.adult', state.select.smartphone.adult);
    },
    value_smartphone_kids: function(state, value) {
      state.breakdown.smartphone_kids = value.cost;
      console.log('state.breakdown.smartphone_kids', state.breakdown.smartphone_kids);
      state.select.smartphone.kids = value.plan;
      console.log('state.select.smartphone.kids', state.select.smartphone.kids);
    },
    value_media_newspaper: function(state, value) {
      state.select.media.newspaper = value;
      console.log('state.select.media.newspaper', state.select.media.newspaper);
      state.breakdown.media_newspaper = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.media_newspaper += value[this.i].cost;
      }
      console.log('state.breakdown.media_newspaper', state.breakdown.media_newspaper);
    },
    value_media_tv_radio: function(state, value) {
      state.select.media.tv_radio = value;
      console.log('state.select.media.tv_radio', state.select.media.tv_radio);
      state.breakdown.media_tv_radio = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.media_tv_radio += value[this.i].cost;
      }
      console.log('state.breakdown.media_tv_radio', state.breakdown.media_tv_radio);
    },
    value_media_books: function(state, value) {
      state.select.media.books = value;
      console.log('state.select.media.books', state.select.media.books);
      state.breakdown.media_books = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.media_books += value[this.i].cost;
      }
      console.log('state.breakdown.media_books', state.breakdown.media_books);
    },
    value_media_web: function(state, value) {
      state.select.media.web = value;
      console.log('state.select.media.web', state.select.media.web);
      state.breakdown.media_web = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.media_web += value[this.i].cost;
      }
      console.log('state.breakdown.media_web', state.breakdown.media_web);
    },
    value_media_isp: function(state, value) {
      state.select.media.isp = value;
      console.log('state.select.media.isp', state.select.media.isp);
      state.breakdown.media_isp = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.media_isp += value[this.i].cost;
      }
      console.log('state.breakdown.media_isp', state.breakdown.media_isp);
    },
    value_daily_necessities_dental: function(state, value) {
      state.select.daily_necessities.dental = value;
      console.log('state.select.daily_necessities.dental', state.select.daily_necessities.dental);
      state.breakdown.daily_necessities_dental = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.daily_necessities_dental += value[this.i].cost;
      }
      console.log('state.breakdown.daily_necessities_dental', state.breakdown.daily_necessities_dental);
    },
    value_daily_necessities_laundry: function(state, value) {
      state.select.daily_necessities.laundry = value;
      console.log('state.select.daily_necessities.laundry', state.select.daily_necessities.laundry);
      state.breakdown.daily_necessities_laundry = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.daily_necessities_laundry += value[this.i].cost;
      }
      console.log('state.breakdown.daily_necessities_laundry', state.breakdown.daily_necessities_laundry);
    },
    value_daily_necessities_sanitary: function(state, value) {
      state.select.daily_necessities.sanitary = value;
      console.log('state.select.daily_necessities.sanitary', state.select.daily_necessities.sanitary);
      state.breakdown.daily_necessities_sanitary = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.daily_necessities_sanitary += value[this.i].cost;
      }
      console.log('state.breakdown.daily_necessities_sanitary', state.breakdown.daily_necessities_sanitary);
    },
    value_daily_necessities_bath: function(state, value) {
      state.select.daily_necessities.bath = value;
      console.log('state.select.daily_necessities.bath', state.select.daily_necessities.bath);
      state.breakdown.daily_necessities_bath = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.daily_necessities_bath += value[this.i].cost;
      }
      console.log('state.breakdown.daily_necessities_bath', state.breakdown.daily_necessities_bath);
    },
    value_daily_necessities_kitchen: function(state, value) {
      state.select.daily_necessities.kitchen = value;
      console.log('state.select.daily_necessities.kitchen', state.select.daily_necessities.kitchen);
      state.breakdown.daily_necessities_kitchen = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.daily_necessities_kitchen += value[this.i].cost;
      }
      console.log('state.breakdown.daily_necessities_kitchen', state.breakdown.daily_necessities_kitchen);
    },
    value_daily_necessities_etc: function(state, value) {
      state.select.daily_necessities.etc = value;
      console.log('state.select.daily_necessities.etc', state.select.daily_necessities.etc);
      state.breakdown.daily_necessities_etc = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.daily_necessities_etc += value[this.i].cost;
      }
      console.log('state.breakdown.daily_necessities_etc', state.breakdown.daily_necessities_etc);
    },
    value_medical_medicine_internal: function(state, value) {
      state.select.medical_medicine.internal = value;
      console.log('state.select.medical_medicine.internal', state.select.medical_medicine.internal);
      state.breakdown.medical_medicine_internal = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.medical_medicine_internal += value[this.i].cost;
      }
      console.log('state.breakdown.medical_medicine_internal', state.breakdown.medical_medicine_internal);
    },
    value_medical_medicine_analgesic: function(state, value) {
      state.select.medical_medicine.analgesic = value;
      console.log('state.select.medical_medicine.analgesic', state.select.medical_medicine.analgesic);
      state.breakdown.medical_medicine_analgesic = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.medical_medicine_analgesic += value[this.i].cost;
      }
      console.log('state.breakdown.medical_medicine_analgesic', state.breakdown.medical_medicine_analgesic);
    },
    value_medical_medicine_external: function(state, value) {
      state.select.medical_medicine.external = value;
      console.log('state.select.medical_medicine.external', state.select.medical_medicine.external);
      state.breakdown.medical_medicine_external = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.medical_medicine_external += value[this.i].cost;
      }
      console.log('state.breakdown.medical_medicine_external', state.breakdown.medical_medicine_external);
    },
    value_medical_medicine_etc: function(state, value) {
      state.select.medical_medicine.etc = value;
      console.log('state.select.medical_medicine.etc', state.select.medical_medicine.etc);
      state.breakdown.medical_medicine_etc = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.medical_medicine_etc += value[this.i].cost;
      }
      console.log('state.breakdown.medical_medicine_etc', state.breakdown.medical_medicine_etc);
    },
    value_medical_medicine_supplement: function(state, value) {
      state.select.medical_medicine.supplement = value;
      console.log('state.select.medical_medicine.supplement', state.select.medical_medicine.supplement);
      state.breakdown.medical_medicine_supplement = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.medical_medicine_supplement += value[this.i].cost;
      }
      console.log('state.breakdown.medical_medicine_supplement', state.breakdown.medical_medicine_supplement);
    },
    value_medical_medicine_children: function(state, value) {
      state.select.medical_medicine.children = value;
      console.log('state.select.medical_medicine.children', state.select.medical_medicine.children);
      state.breakdown.medical_medicine_children = 0;
      for(this.i = 0; this.i < Object.keys(value).length; this.i++){
        state.breakdown.medical_medicine_children += value[this.i].cost;
      }
      console.log('state.breakdown.medical_medicine_children', state.breakdown.medical_medicine_children);
    },
    initialize_medical_physical: function(state, value) {
      state.breakdown.medical_physical = value;
    },
    value_medical_physical: function(state, {index, num}) {
      state.breakdown.medical_physical[index].num = num;
      state.breakdown.medical_physical[index].order = state.breakdown.medical_physical[index].cost * state.breakdown.medical_physical[index].num;
      console.log('state.breakdown.medical_physical.reduce', state.breakdown.medical_physical.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.medical_physical[index] = String(num);
      console.log('state.select.medical_physical[index]', state.select.medical_physical[index]);
    },
    value_saving: function(state, value) {
      state.breakdown.saving = value;
      console.log('state.breakdown.saving', state.breakdown.saving);
      state.select.saving = `${Number(value).toLocaleString()}円`;
      console.log('state.select.saving', state.select.saving);
    },
    value_life_insurance_you: function(state, value) {
      state.breakdown.life_insurance_you = value.premium;
      console.log('state.breakdown.life_insurance_you', state.breakdown.life_insurance_you);
      state.select.life_insurance.you = value.select;
      console.log('state.select.life_insurance.you', state.select.life_insurance.you);
    },
    value_life_insurance_spouse: function(state, value) {
      state.breakdown.life_insurance_spouse = value.premium;
      console.log('state.breakdown.life_insurance_spouse', state.breakdown.life_insurance_spouse);
      state.select.life_insurance.spouse = value.select;
      console.log('state.select.life_insurance.spouse', state.select.life_insurance.spouse);
    },
    initialize_eat_out: function(state, value) {
      state.breakdown.eat_out = value;
    },
    value_eat_out: function(state, {index, num}) {
      state.breakdown.eat_out[index].num = num;
      state.breakdown.eat_out[index].order = state.breakdown.eat_out[index].cost * state.breakdown.eat_out[index].num;
      console.log('state.breakdown.eat_out.reduce', state.breakdown.eat_out.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.eat_out[index] = String(num);
      console.log('state.select.eat_out[index]', state.select.eat_out[index]);
    },
    initialize_lesson_adult: function(state, value) {
      state.breakdown.lesson_adult = value;
    },
    value_lesson_adult: function(state, {index, num}) {
      state.breakdown.lesson_adult[index].num = num;
      state.breakdown.lesson_adult[index].order = state.breakdown.lesson_adult[index].cost * state.breakdown.lesson_adult[index].num;
      console.log('state.breakdown.lesson_adult.reduce', state.breakdown.lesson_adult.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.lesson.adult[index] = String(num);
      console.log('state.select.lesson.adult[index]', state.select.lesson.adult[index]);
    },
    initialize_lesson_child: function(state, value) {
      state.breakdown.lesson_child = value;
    },
    value_lesson_child: function(state, {index, num}) {
      state.breakdown.lesson_child[index].num = num;
      state.breakdown.lesson_child[index].order = state.breakdown.lesson_child[index].cost * state.breakdown.lesson_child[index].num;
      console.log('state.breakdown.lesson_child.reduce', state.breakdown.lesson_child.reduce((sum, i) => { return sum + i.order; }, 0));
      state.select.lesson.child[index] = String(num);
      console.log('state.select.lesson.child[index]', state.select.lesson.child[index]);
    },
    value_emergency_kit: function(state, value) {
      state.breakdown.emergency_kit = value.cost;
      console.log('state.breakdown.emergency_kit', state.breakdown.emergency_kit);
      state.select.emergency_kit = value.name;
      console.log('state.select.emergency_kit', state.select.emergency_kit);
    },
    initialize_trip: function(state, value) {
      state.breakdown.trip = value;
    },
    value_trip_adult: function(state, {index, num}) {
      state.breakdown.trip[index].num_adult = num;
      state.breakdown.trip[index].order_adult = state.breakdown.trip[index].cost_adult * state.breakdown.trip[index].num_adult;
      console.log('state.breakdown.trip.reduce', state.breakdown.trip.reduce((sum, i) => { return sum + i.order_adult + i.order_child; }, 0));
      state.select.trip.adult[index] = '大人料金' + String(num);
      console.log('state.select.trip.adult[index]', state.select.trip.adult[index]);
    },
    value_trip_child: function(state, {index, num}) {
      state.breakdown.trip[index].num_child = num;
      state.breakdown.trip[index].order_child = state.breakdown.trip[index].cost_child * state.breakdown.trip[index].num_child;
      console.log('state.breakdown.trip.reduce', state.breakdown.trip.reduce((sum, i) => { return sum + i.order_child + i.order_adult; }, 0));
      state.select.trip.child[index] = '子ども料金' + String(num);
      console.log('state.select.trip.child[index]', state.select.trip.child[index]);
    },
    set_progress: function(state) {
      state.progresses.progress_house = (state.breakdown.house != 0) ? true : false;
      state.progresses.progress_electricity = (state.breakdown.electricity != 0) ? true : false;
      state.progresses.progress_gas = (state.breakdown.gas != 0) ? true : false;
      state.progresses.progress_water = (state.breakdown.water != 0) ? true : false;
      state.progresses.progress_food = ((state.breakdown.main_food1 + state.breakdown.main_food1 + state.breakdown.main_food2 + state.breakdown.main_food3 + state.breakdown.main_food4 + state.breakdown.side_food1 + state.breakdown.side_food2 + state.breakdown.side_food3 + state.breakdown.side_food4) != 0) ? true : false;
      state.progresses.progress_fashion = ((state.breakdown.fashion_men.reduce((sum, i) => { return sum + i.order; }, 0) + state.breakdown.fashion_women.reduce((sum, i) => { return sum + i.order; }, 0) + state.breakdown.fashion_kids.reduce((sum, i) => { return sum + i.order; }, 0) + state.breakdown.fashion_baby.reduce((sum, i) => { return sum + i.order; }, 0)) != 0) ? true : false;
      state.progresses.progress_car = (state.breakdown.car != 0) ? true : false;
      state.progresses.progress_smartphone = ((state.breakdown.smartphone_adult + state.breakdown.smartphone_kids) != 0) ? true : false;
      state.progresses.progress_media = ((state.breakdown.media_newspaper + state.breakdown.media_tv_radio + state.breakdown.media_books + state.breakdown.media_web + state.breakdown.media_isp) != 0) ? true : false;
      state.progresses.progress_daily_necessities = ((state.breakdown.daily_necessities_dental + state.breakdown.daily_necessities_laundry + state.breakdown.daily_necessities_sanitary + state.breakdown.daily_necessities_bath + state.breakdown.daily_necessities_kitchen + state.breakdown.daily_necessities_etc) != 0) ? true : false;
      state.progresses.progress_medical = ((state.breakdown.medical_medicine_internal + state.breakdown.medical_medicine_analgesic + state.breakdown.medical_medicine_external + state.breakdown.medical_medicine_etc + state.breakdown.medical_medicine_supplement + state.breakdown.medical_medicine_children + state.breakdown.medical_physical.reduce((sum, i) => { return sum + i.order; }, 0)) != 0) ? true : false;
      state.progresses.progress_saving = (state.breakdown.saving != 0) ? true : false;
      state.progresses.progress_life_insurance = ((state.breakdown.life_insurance_you + state.breakdown.life_insurance_spouse) != 0) ? true : false;
      state.progresses.progress_eat_out = ((state.breakdown.eat_out.reduce((sum, i) => { return sum + i.order; }, 0)) != 0) ? true : false;
      state.progresses.progress_lesson = ((state.breakdown.lesson_adult.reduce((sum, i) => { return sum + i.order; }, 0) + state.breakdown.lesson_child.reduce((sum, i) => { return sum + i.order; }, 0)) != 0) ? true : false;
      state.progresses.progress_emergency_kit = (state.breakdown.emergency_kit != 0) ? true : false;
      state.progresses.progress_trip = ((state.breakdown.trip.reduce((sum, i) => { return sum + i.order_adult; }, 0) + state.breakdown.trip.reduce((sum, i) => { return sum + i.order_child; }, 0)) != 0) ? true : false;
      state.progress = state.progresses.progress_house + 
        state.progresses.progress_electricity + 
        state.progresses.progress_gas + 
        state.progresses.progress_water + 
        state.progresses.progress_food + 
        state.progresses.progress_fashion + 
        state.progresses.progress_car + 
        state.progresses.progress_smartphone + 
        state.progresses.progress_media + 
        state.progresses.progress_daily_necessities + 
        state.progresses.progress_medical + 
        state.progresses.progress_saving + 
        state.progresses.progress_life_insurance + 
        state.progresses.progress_eat_out + 
        state.progresses.progress_lesson + 
        state.progresses.progress_emergency_kit + 
        state.progresses.progress_trip;
      console.log('state.progress', state.progress);
    },
    set_target: function(state, value) {
      state.target = value;
      console.log('state.target', state.target);
    },
    set_env: function(state, value) {
      state.env = value;
      console.log('state.env', state.env);
    },
    set_stock_intermediate_text: function(state, index) {
      state.login_response.stock[index].intermediate_text = state.login_response.stock[index].intermediate_price ;
    },
    set_stock_closing_text: function(state, index) {
      state.login_response.stock[index].closing_text = state.login_response.stock[index].closing_price ;
    },
    set_is_debug: function(state, value) {
      state.is_debug = value;
      console.log('state.is_debug', state.is_debug);
    }
  },
  actions: {
  },
  modules: {
  },
  plugins: [
    createPersistedState(
      { // ストレージのキーを指定。デフォルトではvuex
        key: 'financeParkApp',

        // 管理対象のステートを指定。pathsを書かない時は`modules`に書いたモジュールに含まれるステート全て。`[]`の時はどれも保存されない
        // paths: [],

        // ストレージの種類を指定する。デフォルトではローカルストレージ
        storage: window.sessionStorage
      }
    )
  ]
})
